import { JournalEntrySource } from "@common/types/domains/accounting/journalEntry";
import { JournalEntrySourceFilter } from "@common/schemas/domains/accounting/journalEntry";
import { ManualJournalEntrySource } from "@common/types/domains/accounting/manualJournalEntry";

export function getIntegerSourceId(
    journalEntrySource?: JournalEntrySource | null,
): number | null {
    return journalEntrySource?.id && typeof journalEntrySource.id === "number"
        ? journalEntrySource.id
        : null;
}

// Maps each entry-source filter whose lines are identified by a journal_entry
// foreign-key column to that column name. A line matches the filter when the
// column IS NOT NULL. Shared so the General Ledger report and the Counterparties
// directory cannot drift in how they interpret the same filter.
export const JOURNAL_ENTRY_SOURCE_FILTER_COLUMNS: Partial<
    Record<JournalEntrySourceFilter, string>
> = {
    [JournalEntrySourceFilter.Transaction]: "transactionId",
    [JournalEntrySourceFilter.Loan]: "loanId",
    [JournalEntrySourceFilter.OpeningBalance]: "openingBalanceId",
    [JournalEntrySourceFilter.Bill]: "billId",
    [JournalEntrySourceFilter.Invoice]: "invoiceId",
};

// Maps each entry-source filter whose lines are identified by
// manual_journal_entry.source to the concrete source values it covers.
export const JOURNAL_ENTRY_SOURCE_FILTER_MJE_SOURCES: Partial<
    Record<JournalEntrySourceFilter, ManualJournalEntrySource[]>
> = {
    [JournalEntrySourceFilter.ManualJournalEntry]: [
        ManualJournalEntrySource.USER,
        ManualJournalEntrySource.API,
        ManualJournalEntrySource.RECURRING,
    ],
    [JournalEntrySourceFilter.QuickBooksOnline]: [
        ManualJournalEntrySource.QUICKBOOKS,
    ],
    [JournalEntrySourceFilter.QuickBooksDesktop]: [
        ManualJournalEntrySource.QUICKBOOKS_DESKTOP,
    ],
    [JournalEntrySourceFilter.Xero]: [ManualJournalEntrySource.XERO],
};

export interface ResolvedJournalEntrySourceFilters {
    /** journal_entry FK columns that must be NOT NULL (any one matches). */
    notNullColumns: string[];
    /** manual_journal_entry.source values that match (any one matches). */
    manualJournalEntrySources: ManualJournalEntrySource[];
    /** Whether recurring lines (manual_journal_entry.recurringRuleId IS NOT NULL) match. */
    includesRecurring: boolean;
    /**
     * Whether migrated lines from systems without a dedicated filter match:
     * manual_journal_entry.importedJournalEntryId IS NOT NULL AND
     * manual_journal_entry.source NOT IN (IMPORTED_MANUAL_JOURNAL_ENTRY_SOURCES).
     * Matches nothing today (every conversion path writes one of the named
     * sources) but catches future migration systems without filter changes.
     */
    includesMigratedFallback: boolean;
}

/**
 * Resolves the selected entry-source filters into the concrete SQL building
 * blocks (journal_entry columns, manual_journal_entry sources, recurring flag)
 * shared by every query that filters journal-entry lines by source. Each call
 * site renders these blocks with its own table aliases/join strategy, but the
 * enum→source mapping lives here so the filters cannot diverge.
 */
export const resolveJournalEntrySourceFilters = (
    sourceFilters: JournalEntrySourceFilter[],
): ResolvedJournalEntrySourceFilters => {
    const notNullColumns: string[] = [];
    const manualJournalEntrySources: ManualJournalEntrySource[] = [];

    for (const filter of sourceFilters) {
        const column = JOURNAL_ENTRY_SOURCE_FILTER_COLUMNS[filter];
        if (column) {
            notNullColumns.push(column);
        }
        const mjeSources = JOURNAL_ENTRY_SOURCE_FILTER_MJE_SOURCES[filter];
        if (mjeSources) {
            manualJournalEntrySources.push(...mjeSources);
        }
    }

    return {
        notNullColumns,
        manualJournalEntrySources,
        includesRecurring: sourceFilters.includes(
            JournalEntrySourceFilter.Recurring,
        ),
        includesMigratedFallback: sourceFilters.includes(
            JournalEntrySourceFilter.MigratedData,
        ),
    };
};
